import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  stopTutorial,
  setIsLoading,
  setNavigating,
  setCurrentStep,
} from "../../Redux/tutorialSlice";
import tutorialSteps from "./tutorialSteps";
import { useLocation } from "react-router-dom";
import { Spin } from "antd";
import "./styles.css";
import useCutout from "./useCutout";
import useTutorialSteps from "./useTutorialSteps";
import { isTablet } from "../../utils/helperFunctions";

function TutorialManager() {
  const location = useLocation();
  const dispatch = useDispatch();
  const isTutorialActive = useSelector(
    (state) => state.tutorial.isTutorialActive
  );
  const currentStep = useSelector((state) => state.tutorial.currentStep);
  const navigating = useSelector((state) => state.tutorial.navigating);
  const direction = useSelector((state) => state.tutorial.direction);
  const isLoading = useSelector((state) => state.tutorial.isLoading);
  const controlsPosition = useSelector(
    (state) => state.tutorial.controlsPosition
  );
  const [initialLoad, setInitialLoad] = useState(true);
  const [isNavigatingStep, setIsNavigatingStep] = useState(false);
  const step = tutorialSteps[currentStep] || {};
  const { updateCutout } = useCutout();

  const { handleNextStep, handlePrevStep, processStep } = useTutorialSteps();

  // Define all selectors to disable scroll on
  const scrollBlockedSelectors = ["body", ".practice-list", ".ant-modal-body"];

  // Utility: Add/remove disable-scroll to multiple targets
  const setScrollBlocking = (enabled) => {
    scrollBlockedSelectors.forEach((selector) => {
      const el = document.querySelector(selector);
      if (el) {
        el.classList.toggle("disable-scroll", !enabled);
      }
    });
  };

  // Always clean on mount
  useEffect(() => {
    setScrollBlocking(true); // Allow scroll by default
    document.body.classList.remove("tutorial-cutout");
  }, []);

  useEffect(() => {
    if (!isTutorialActive) {
      setScrollBlocking(true); // enable scroll again
      document.body.classList.remove("tutorial-cutout");
    }
  }, [isTutorialActive]);

  useEffect(() => {
    if (!isTutorialActive) return;

    const scrollable = step?.canScroll === true;
    setScrollBlocking(scrollable);

    return () => {
      setScrollBlocking(true); // always enable scroll on cleanup
      document.body.classList.remove("tutorial-cutout");
    };
  }, [step, isTutorialActive]);

  useEffect(() => {
    const runUpdate = async () => {
      if (isTutorialActive && step?.target) {
        if (initialLoad) dispatch(setIsLoading(false));
        setInitialLoad(false);
        await updateCutout(step.target, step.advanceType, {
          manageLoading: true,
        });
      }
    };
    runUpdate();
  }, [isTutorialActive, step]);

  useEffect(() => {
    if (!navigating) return;

    const targetStepIndex =
      direction === "next"
        ? Math.min(currentStep + 1, tutorialSteps.length - 1)
        : direction === "prev"
        ? Math.max(currentStep - 1, 0)
        : currentStep;

    if (targetStepIndex >= 0 && targetStepIndex < tutorialSteps.length) {
      processStep(targetStepIndex, 0);
    }

    dispatch(setNavigating(false));
  }, [navigating, location.pathname]);

  useEffect(() => {
    if (!isTutorialActive) return;

    const currentTutorialStep = tutorialSteps[currentStep];
    const currentCondition = currentTutorialStep?.condition;

    if (!currentCondition) return;

    const isConditionValid = () => {
      if (currentCondition === "hasMostCommon") {
        return !!document.querySelector(".tutorial-most-common-card");
      }
      if (currentCondition === "hasCrep") {
        return !!document.querySelector(".tutorial-crep-card");
      }
      return true;
    };

    requestAnimationFrame(() => {
      if (isConditionValid()) return;

      const moveForward = direction !== "prev";
      const stepIncrement = moveForward ? 1 : -1;
      let nextValidStepIndex = currentStep + stepIncrement;

      while (
        nextValidStepIndex >= 0 &&
        nextValidStepIndex < tutorialSteps.length
      ) {
        const nextStep = tutorialSteps[nextValidStepIndex];
        const nextCondition = nextStep?.condition;

        if (!nextCondition) {
          dispatch(setCurrentStep(nextValidStepIndex));
          break;
        }

        const conditionMet =
          (nextCondition === "hasMostCommon" &&
            !!document.querySelector(".tutorial-most-common-card")) ||
          (nextCondition === "hasCrep" &&
            !!document.querySelector(".tutorial-crep-card"));

        if (conditionMet) {
          dispatch(setCurrentStep(nextValidStepIndex));
          break;
        }

        nextValidStepIndex += stepIncrement;
      }
    });
  }, [currentStep, direction, isTutorialActive, dispatch]);

  const handleSkipTutorial = () => dispatch(stopTutorial());

  if (!isTutorialActive) return null;

  return (
    <>
      <div className="tutorial-blocker tutorial-top" />
      <div className="tutorial-blocker tutorial-bottom" />
      <div className="tutorial-blocker tutorial-left" />
      <div className="tutorial-blocker tutorial-right" />
      <div className="tutorial-outline" />
      <div
        className={`tutorial-content ${isLoading ? "loading" : ""}`}
        role="dialog"
        aria-labelledby="tutorial-title"
        data-position={controlsPosition}
        style={{ left: "var(--tutorial-left)" }}
      >
        <div className="tutorial-header">
          <h3 id="tutorial-title">{step.title || "Step"}</h3>
        </div>

        <div className="tutorial-body">
          {isLoading ? (
            <div className="loading-container">
              <Spin size="large" />
              <p>Loading next step...</p>
            </div>
          ) : (
            <div className="tutorial-text">
              {isTablet() && step.tabletText ? step.tabletText : step.text}
            </div>
          )}
          <span className="skip-tutorial" onClick={handleSkipTutorial}>
            Skip Tutorial
          </span>
        </div>

        <div className="tutorial-footer">
          {currentStep > 0 && (
            <button className="previous-button" onClick={handlePrevStep}>
              Previous
            </button>
          )}
          {step.advanceType === "next" && (
            <button onClick={handleNextStep}>
              {currentStep < tutorialSteps.length - 1
                ? "Next"
                : "Finish Tutorial"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default TutorialManager;
